import { RefObject } from 'react';

import {
  AttributeWeight,
  GraphEntityType,
  VisibilityState,
} from '@/components/nodeGraph/utils/types';
import { ResourceType } from '@/types';

/**
 * Individual cluster representation
 */
export interface Cluster {
  value: string;
  nodes: string[];
  color: string;
  count?: number; // Optional total count from backend (for display)
}

/**
 * Tree node structure for visibility hierarchy
 */
export interface VisibleNode {
  id: string;
  label: string;
  children?: VisibleNode[];
  isParent?: boolean;
}

/**
 * Props for the main ClusterWeightControls component
 *
 * **BREAKING CHANGE (Phase 3)**: Most props have been removed in favor of React Context.
 * The component now uses GraphStateProvider and GraphDataProvider for state management.
 *
 * Remaining props:
 * - attributes: List of attribute names (from constants)
 * - clusters: Computed cluster data (from useClusterManagement)
 * - filters/setFilters: Parent-level filters (from Assets section)
 */
export interface ClusterWeightControlsProps {
  /** List of attribute names for visibility controls */
  attributes: string[];

  /** Computed clusters from useClusterManagement hook */
  clusters: Cluster[];

  /** Parent-level filters (optional, from Assets section) */
  filters?: any;

  /** Callback to update parent-level filters */
  setFilters?: (filters: any) => void;
}

/**
 * Props for FiltersSection component
 */
export interface FiltersSectionProps {
  filters: any;
  setFilters: (filters: any) => void;
  clusterByEntity: GraphEntityType;
  isExpanded: boolean;
  onToggle: () => void;
  panelRef: RefObject<HTMLDivElement | null>;
}

/**
 * Props for ClusteringSection component
 */
export interface ClusteringSectionProps {
  currentWeights: AttributeWeight[];
  onWeightChange: (name: string, weight: number) => void;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * Props for ActiveClustersSection component
 */
export interface ActiveClustersSectionProps {
  clusters: Cluster[];
  selectedClusterValue?: string | null;
  onClusterClick: (value: string, nodes: string[]) => void;
  activeAttribute?: string;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * Props for NodesVisibilitySection component
 */
export interface NodesVisibilitySectionProps {
  attributes: string[];
  visibilityState: VisibilityState;
  onVisibilityStateChange: (state: VisibilityState) => void;
}

/**
 * Props for MaxNodesInput component
 */
export interface MaxNodesInputProps {
  maxNodes: number;
  onMaxNodesChange?: (maxNodes: number) => void;
}

/**
 * Helper function to create visibility tree structure
 * Organizes entity types and attributes into a hierarchical structure
 */
export const createVisibilityTree = (attributes: string[]): VisibleNode[] => [
  {
    id: ResourceType.Risk,
    label: 'Vulnerabilities',
    isParent: true,
  },
  {
    id: ResourceType.Technology,
    label: 'Technologies',
    isParent: true,
  },
  {
    id: ResourceType.Webpage,
    label: 'Webpages',
    isParent: true,
  },
  {
    id: ResourceType.Seed,
    label: 'Seeds',
    isParent: true,
  },
  {
    id: 'Tag',
    label: 'Tags',
    isParent: true,
  },
  {
    id: ResourceType.Attribute,
    label: 'Attributes',
    isParent: true,
    children: attributes.map(attr => ({
      id: attr,
      label: attr,
      isParent: false,
    })),
  },
];

/**
 * Helper function to check if all children in a node are selected
 * Used for determining parent checkbox state
 */
export const areAllChildrenSelected = (
  node: VisibleNode,
  visibilityState: VisibilityState
): boolean => {
  if (!node.children) {
    // For parent nodes without children, check the entity type visibility
    return visibilityState[node.id] ?? true;
  }
  return node.children.every(child => visibilityState[child.id] ?? true);
};

/**
 * Helper function to handle parent checkbox toggle
 * Toggles parent and all children based on current selection state
 */
export const handleParentToggle = (
  node: VisibleNode,
  visibilityState: VisibilityState,
  onVisibilityStateChange: (state: VisibilityState) => void
) => {
  const newState = { ...visibilityState };

  if (!node.children) {
    // For entity nodes without children, toggle the entity visibility
    newState[node.id] = !visibilityState[node.id];
  } else {
    // For nodes with children (like Attributes), toggle all children
    const allSelected = areAllChildrenSelected(node, visibilityState);
    node.children.forEach(child => {
      newState[child.id] = !allSelected;
    });
    // Also set the parent state
    newState[node.id] = !allSelected;
  }

  onVisibilityStateChange(newState);
};
